import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Tooltip, Typography, Divider } from '@mui/material';
import { SxProps } from '@mui/system';
import {
  DragIndicator,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from '@mui/icons-material';
import { ReactComponent as ConfigIcon } from '../../assets/20x20/config.svg';
import useUserPreferences, {
  TableDensity,
} from '../../hooks/useUserPreferences';
import IconButton from '../IconButton';
import Checkbox from '../Checkbox';
import DropDown, { DropDownOption } from '../DropDown';
import { QuickFilterChip } from '../Chip';
import DisplayPopover, {
  DisplayPopoverChips,
  DisplayPopoverOptionLabel,
  DisplayPopoverSectionLabel,
} from '../DisplayPopover';
import { Flex, FlexColumn } from '../Flex';
import OverflowTooltip from '../OverflowTooltip/OverflowTooltip';
import type { Column } from './types';
import { applySavedColumnOrder } from './utils';

type ColumnSection = 'visible' | 'hidden';

const ColumnsScrollArea = styled(FlexColumn)`
  max-height: 320px;
  overflow-y: auto;
`;

const ColumnRowWrapper = styled(Flex)<{
  $isDragging?: boolean;
  $draggable?: boolean;
}>`
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  cursor: ${({ $draggable }) => ($draggable ? 'grab' : 'default')};
  background-color: ${({ theme, $isDragging }) =>
    $isDragging ? theme.palette.common.gray9 : 'transparent'};
  opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};
  will-change: transform;

  &:hover {
    background-color: ${({ theme }) => theme.palette.common.gray9};
  }

  &:active {
    cursor: ${({ $draggable }) => ($draggable ? 'grabbing' : 'default')};
  }
`;

const DragHandle = styled(Flex)<{ $hidden?: boolean }>`
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.palette.common.gray2};
  opacity: 0.3;
  transition: opacity 0.1s;
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};

  ${ColumnRowWrapper}:hover & {
    opacity: ${({ $hidden }) => ($hidden ? 0.3 : 0.7)};
  }
`;

const HiddenDisclosure = styled(Flex)`
  align-items: center;
  gap: 4px;
  padding: 12px 16px 6px;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.common.gray2};
  font-size: 12px;
  font-weight: 500;
  user-select: none;

  &:hover {
    color: ${({ theme }) => theme.palette.common.black};
  }
`;

const GroupByHint = styled(Typography)`
  font-size: 11px;
  color: ${({ theme }) => theme.palette.common.gray2};
  padding: 0 16px 8px;
`;

type ColumnRowProps<T> = {
  column: Column<T>;
  isVisible: boolean;
  section: ColumnSection;
  isDragging: boolean;
  registerRef: (id: string, el: HTMLDivElement | null) => void;
  onToggleVisible: () => void;
  onDragStart: (e: React.DragEvent, columnId: string) => void;
  onDragOver: (e: React.DragEvent, columnId: string) => void;
  onDrop: (e: React.DragEvent, columnId: string) => void;
  onDragEnd: () => void;
};

const ColumnRow = <T extends object>({
  column,
  isVisible,
  section,
  isDragging,
  registerRef,
  onToggleVisible,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: ColumnRowProps<T>) => {
  const isDraggable = section !== 'hidden' && !column.sticky;

  return (
    <ColumnRowWrapper
      ref={(el: HTMLDivElement | null) => registerRef(column.id, el)}
      $isDragging={isDragging}
      $draggable={isDraggable}
      draggable={isDraggable}
      onDragStart={isDraggable ? (e) => onDragStart(e, column.id) : undefined}
      onDragOver={isDraggable ? (e) => onDragOver(e, column.id) : undefined}
      onDrop={isDraggable ? (e) => onDrop(e, column.id) : undefined}
      onDragEnd={isDraggable ? onDragEnd : undefined}
      data-section={section}
    >
      <DragHandle
        $hidden={!isDraggable}
        title={isDraggable ? 'Drag to reorder' : undefined}
      >
        <DragIndicator sx={{ fontSize: 14 }} />
      </DragHandle>
      <Checkbox
        $noPadding
        checked={isVisible}
        onChange={onToggleVisible}
        onClick={(e) => e.stopPropagation()}
        sx={{ p: 0 }}
        inputProps={{ 'aria-label': column.header }}
      />
      <DisplayPopoverOptionLabel>
        <OverflowTooltip>{column.header}</OverflowTooltip>
      </DisplayPopoverOptionLabel>
    </ColumnRowWrapper>
  );
};

const DENSITY_OPTIONS: { value: TableDensity; label: string }[] = [
  { value: 'compact', label: 'Compact' },
  { value: 'regular', label: 'Regular' },
];

const DEFAULT_DENSITY: TableDensity = 'regular';

const FLIP_DURATION_MS = 180;

const ordersEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id, idx) => id === b[idx]);

type Props<T> = {
  sx?: SxProps;
  columns: Column<T>[];
  localStorageKey: string;
  groupBy?: string | null;
  onGroupByChange?: (columnId: string | null) => void;
  groupableColumnIds?: string[];
  allowUngrouped?: boolean;
};

const DisplayPanel = <T extends object>({
  sx,
  columns,
  localStorageKey,
  groupBy,
  onGroupByChange,
  groupableColumnIds,
  allowUngrouped = true,
}: Props<T>) => {
  const { userPreferences, updateUserPreferences } = useUserPreferences();

  const toggleableColumns = useMemo(
    () => columns.filter((c) => !c.nonToggleable && !c.hideColumn),
    [columns],
  );

  const defaultVisibleIds = useMemo(
    () => toggleableColumns.filter((c) => !c.defaultUntoggled).map((c) => c.id),
    [toggleableColumns],
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hiddenOpen, setHiddenOpen] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOrder, setDragOrder] = useState<string[] | null>(null);

  const density: TableDensity =
    userPreferences.density?.[localStorageKey] || DEFAULT_DENSITY;
  const setDensity = useCallback(
    (next: TableDensity) => {
      updateUserPreferences('density', {
        ...(userPreferences.density || {}),
        [localStorageKey]: next,
      });
    },
    [updateUserPreferences, userPreferences.density, localStorageKey],
  );

  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const prevRects = useRef(new Map<string, DOMRect>());
  const flipTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const columnsScrollRef = useRef<HTMLDivElement>(null);
  const hiddenDisclosureRef = useRef<HTMLDivElement>(null);
  const wasHiddenSectionShown = useRef(false);

  const registerRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) rowRefs.current.set(id, el);
    else rowRefs.current.delete(id);
  }, []);

  const visibleSet = useMemo(() => {
    const stored = userPreferences.visibleColumns?.[localStorageKey];
    return new Set(stored ?? defaultVisibleIds);
  }, [userPreferences.visibleColumns, localStorageKey, defaultVisibleIds]);

  const isColumnVisible = useCallback(
    (c: Column<T>) => !!c.nonToggleable || visibleSet.has(c.id),
    [visibleSet],
  );

  const groupByOptions = useMemo(
    () =>
      columns.filter(
        (c) =>
          !c.metaColumn &&
          !c.hideColumn &&
          (!groupableColumnIds || groupableColumnIds.includes(c.id)),
      ),
    [columns, groupableColumnIds],
  );

  const groupByDropDownOptions = useMemo<DropDownOption[]>(
    () => [
      ...(allowUngrouped ? [{ value: '', label: 'None' }] : []),
      ...groupByOptions.map((c) => ({
        value: c.id,
        label: isColumnVisible(c) ? c.header : `${c.header} (hidden)`,
      })),
    ],
    [groupByOptions, isColumnVisible, allowUngrouped],
  );

  const baseOrder = useMemo(() => {
    const base = toggleableColumns.map((c) => c.id);
    const saved = userPreferences.columnOrder?.[localStorageKey];
    if (!saved || saved.length === 0) return base;
    const savedFiltered = saved.filter((id) => base.includes(id));
    const remaining = base.filter((id) => !savedFiltered.includes(id));
    return [...savedFiltered, ...remaining];
  }, [toggleableColumns, userPreferences.columnOrder, localStorageKey]);

  const effectiveOrder = dragOrder ?? baseOrder;

  const orderedColumns = useMemo(
    () => applySavedColumnOrder(toggleableColumns, effectiveOrder),
    [toggleableColumns, effectiveOrder],
  );

  const { visibleColumns, hiddenColumns } = useMemo(() => {
    const visible: Column<T>[] = [];
    const hidden: Column<T>[] = [];
    orderedColumns.forEach((c) => {
      if (visibleSet.has(c.id)) visible.push(c);
      else hidden.push(c);
    });
    return { visibleColumns: visible, hiddenColumns: hidden };
  }, [orderedColumns, visibleSet]);

  const isHiddenSectionShown = hiddenOpen && hiddenColumns.length > 0;

  useLayoutEffect(() => {
    const scrollArea = columnsScrollRef.current;
    const disclosure = hiddenDisclosureRef.current;
    const wasShown = wasHiddenSectionShown.current;
    wasHiddenSectionShown.current = isHiddenSectionShown;

    if (wasShown || !isHiddenSectionShown || !scrollArea || !disclosure) return;

    scrollArea.scrollTop +=
      disclosure.getBoundingClientRect().top -
      scrollArea.getBoundingClientRect().top;
  }, [isHiddenSectionShown]);

  const sectionOf = useCallback(
    (id: string): ColumnSection => (visibleSet.has(id) ? 'visible' : 'hidden'),
    [visibleSet],
  );

  const handleToggleVisible = useCallback(
    (id: string) => {
      const next = new Set(visibleSet);
      if (next.has(id)) {
        next.delete(id);
        setHiddenOpen(true);
      } else {
        next.add(id);
      }
      const nextIds = toggleableColumns
        .filter((c) => next.has(c.id))
        .map((c) => c.id);
      updateUserPreferences('visibleColumns', {
        ...(userPreferences.visibleColumns || {}),
        [localStorageKey]: nextIds,
      });
    },
    [
      visibleSet,
      toggleableColumns,
      updateUserPreferences,
      userPreferences.visibleColumns,
      localStorageKey,
    ],
  );

  const persistOrder = useCallback(
    (order: string[]) => {
      updateUserPreferences('columnOrder', {
        ...(userPreferences.columnOrder || {}),
        [localStorageKey]: order,
      });
    },
    [updateUserPreferences, userPreferences.columnOrder, localStorageKey],
  );

  const captureRects = useCallback(() => {
    const rects = new Map<string, DOMRect>();
    rowRefs.current.forEach((el, id) =>
      rects.set(id, el.getBoundingClientRect()),
    );
    prevRects.current = rects;
  }, []);

  const clearFlipState = useCallback(() => {
    flipTimers.current.forEach((timer) => clearTimeout(timer));
    flipTimers.current.clear();
    rowRefs.current.forEach((el) => {
      el.style.pointerEvents = '';
    });
  }, []);

  useLayoutEffect(() => {
    const prev = prevRects.current;
    if (prev.size === 0) return;
    rowRefs.current.forEach((el, id) => {
      if (id === draggingId) return;
      const before = prev.get(id);
      if (!before) return;
      const dy = before.top - el.getBoundingClientRect().top;
      if (!dy) return;
      el.style.transition = 'none';
      el.style.transform = `translateY(${dy}px)`;
      el.style.pointerEvents = 'none';
      const existing = flipTimers.current.get(id);
      if (existing) clearTimeout(existing);
      flipTimers.current.set(
        id,
        setTimeout(() => {
          el.style.pointerEvents = '';
          flipTimers.current.delete(id);
        }, FLIP_DURATION_MS),
      );
      requestAnimationFrame(() => {
        el.style.transition = `transform ${FLIP_DURATION_MS}ms ease`;
        el.style.transform = '';
      });
    });
    prevRects.current = new Map();
  }, [effectiveOrder, draggingId]);

  const handleDragStart = useCallback(
    (e: React.DragEvent, id: string) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', id);
      setDraggingId(id);
      setDragOrder(baseOrder);
    },
    [baseOrder],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent, overId: string) => {
      if (!draggingId || draggingId === overId) return;
      if (sectionOf(draggingId) !== sectionOf(overId)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';

      const current = dragOrder ?? baseOrder;
      const from = current.indexOf(draggingId);
      const to = current.indexOf(overId);
      if (from < 0 || to < 0 || from === to) return;

      const overEl = rowRefs.current.get(overId);
      if (!overEl) return;
      const rect = overEl.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;
      const draggingDown = to > from;
      if (draggingDown && e.clientY < midpoint) return;
      if (!draggingDown && e.clientY > midpoint) return;

      captureRects();
      const next = [...current];
      next.splice(from, 1);
      next.splice(to, 0, draggingId);
      setDragOrder(next);
    },
    [draggingId, dragOrder, baseOrder, sectionOf, captureRects],
  );

  const finishDrag = useCallback(() => {
    setDragOrder((current) => {
      if (current && !ordersEqual(current, baseOrder)) {
        persistOrder(current);
      }
      return null;
    });
    setDraggingId(null);
    clearFlipState();
  }, [baseOrder, persistOrder, clearFlipState]);

  const handleDragEnd = useCallback(() => {
    finishDrag();
  }, [finishDrag]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      finishDrag();
    },
    [finishDrag],
  );

  const handleReset = useCallback(() => {
    updateUserPreferences('visibleColumns', {
      ...(userPreferences.visibleColumns || {}),
      [localStorageKey]: defaultVisibleIds,
    });
    persistOrder(toggleableColumns.map((c) => c.id));
    setDragOrder(null);
    setDensity(DEFAULT_DENSITY);
    onGroupByChange?.(null);
    setHiddenOpen(false);
  }, [
    updateUserPreferences,
    userPreferences.visibleColumns,
    localStorageKey,
    defaultVisibleIds,
    persistOrder,
    toggleableColumns,
    setDensity,
    onGroupByChange,
  ]);

  return (
    <>
      <Tooltip title="Display">
        <span>
          <IconButton
            sx={{ p: 0.5, ...sx }}
            aria-label="Display panel"
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <ConfigIcon />
          </IconButton>
        </span>
      </Tooltip>

      <DisplayPopover
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        title="Display"
        footerHint="Saved per table"
        onReset={handleReset}
      >
        <DisplayPopoverSectionLabel>Columns</DisplayPopoverSectionLabel>

        <ColumnsScrollArea ref={columnsScrollRef}>
          <FlexColumn>
            {visibleColumns.map((c) => (
              <ColumnRow
                key={c.id}
                column={c}
                isVisible
                section="visible"
                isDragging={draggingId === c.id}
                registerRef={registerRef}
                onToggleVisible={() => handleToggleVisible(c.id)}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
              />
            ))}
          </FlexColumn>

          {visibleColumns.length === 0 && (
            <Typography
              sx={{
                px: 2,
                py: 1,
                fontSize: 12,
                color: 'common.gray2',
              }}
            >
              No visible columns. Show some below.
            </Typography>
          )}

          {hiddenColumns.length > 0 && (
            <>
              <HiddenDisclosure
                ref={hiddenDisclosureRef}
                onClick={() => setHiddenOpen((o) => !o)}
              >
                {hiddenOpen ? (
                  <KeyboardArrowDown sx={{ fontSize: 16 }} />
                ) : (
                  <KeyboardArrowRight sx={{ fontSize: 16 }} />
                )}
                Hidden ({hiddenColumns.length})
              </HiddenDisclosure>
              {hiddenOpen && (
                <FlexColumn>
                  {hiddenColumns.map((c) => (
                    <ColumnRow
                      key={c.id}
                      column={c}
                      isVisible={false}
                      section="hidden"
                      isDragging={draggingId === c.id}
                      registerRef={registerRef}
                      onToggleVisible={() => handleToggleVisible(c.id)}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onDragEnd={handleDragEnd}
                    />
                  ))}
                </FlexColumn>
              )}
            </>
          )}
        </ColumnsScrollArea>

        <Divider sx={{ mt: 1.5 }} />

        <DisplayPopoverSectionLabel>Group by</DisplayPopoverSectionLabel>
        <DropDown
          sx={{ mx: 2, mt: 0.5, mb: 1 }}
          placeholder="None"
          value={groupBy ?? ''}
          options={groupByDropDownOptions}
          onChange={(value: string) => onGroupByChange?.(value || null)}
          sortOptions={false}
          showClear={false}
        />
        <GroupByHint>
          Categorical columns work best. Hidden columns can still be the group
          key.
        </GroupByHint>

        <Divider sx={{ mt: 0.5 }} />

        <DisplayPopoverSectionLabel>Row density</DisplayPopoverSectionLabel>
        <DisplayPopoverChips>
          {DENSITY_OPTIONS.map((opt) => (
            <QuickFilterChip
              key={opt.value}
              value={opt.value}
              filter={density}
              label={opt.label}
              onClick={(v) => setDensity(v as TableDensity)}
            />
          ))}
        </DisplayPopoverChips>
      </DisplayPopover>
    </>
  );
};

export default DisplayPanel;
